import React, { useState } from 'react';
import { Stack, Text, Group, Box, Card, Progress, ScrollArea, Modal, Divider, Image, Button } from '@mantine/core';
import { Bell, TrendingUp, Calendar, ChevronRight, ChevronLeft, Download, ExternalLink, Megaphone } from 'lucide-react';
import { PropertyCard } from '../components/PropertyCard';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import type { Property, Announcement } from '../types';
interface DashboardProps {
  onPropertyClick?: (propertyId: string) => void;
  onViewAllProperties?: () => void;
}
// Mock data
const mockProperties: Property[] = [{
  id: '1',
  name: 'Marina Heights - Unit 1204',
  unitId: '1204',
  floor: 12,
  area: 1250,
  areaUnit: 'sqft',
  projectName: 'Marina Heights',
  image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&auto=format&fit=crop',
  nextInstallment: {
    amount: 125000,
    dueDate: '2024-02-15'
  },
  constructionProgress: 68
}, {
  id: '2',
  name: 'Palm Residences - Villa 7',
  unitId: 'V7',
  floor: 0,
  area: 3200,
  areaUnit: 'sqft',
  projectName: 'Palm Residences',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&auto=format&fit=crop',
  nextInstallment: {
    amount: 450000,
    dueDate: '2024-03-01'
  },
  constructionProgress: 45
}];
const mockAnnouncements: Announcement[] = [{
  id: '1',
  title: 'Construction Milestone Achieved - 70% Complete',
  summary: 'We are pleased to announce that Marina Heights has reached 70% construction completion, ahead of schedule.',
  content: `We are thrilled to share that Marina Heights has achieved a significant milestone, reaching 70% construction completion - two weeks ahead of our projected timeline.

**Key Achievements:**
- All structural work for floors 1-15 completed
- MEP installations progressing smoothly
- Facade work commenced on lower floors
- Interior finishing work initiated in completed sections

**What This Means For You:**
Your investment is progressing exceptionally well, and we remain committed to delivering your dream home on time. Our quality assurance team continues to monitor every aspect of construction to ensure the highest standards.

**Next Steps:**
- Floors 16-20 structural work (Expected completion: March 2024)
- Complete MEP installations (Expected completion: April 2024)
- Interior finishing acceleration (May-June 2024)

We will continue to provide regular updates on the construction progress. Thank you for your continued trust in our project.`,
  category: 'construction',
  priority: 'high',
  publishedAt: '2024-01-20T10:00:00Z',
  image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&auto=format&fit=crop'
}];
export function Dashboard({
  onPropertyClick,
  onViewAllProperties
}: DashboardProps) {
  const {
    t,
    isRTL
  } = useLanguage();
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const totalProgress = Math.round(mockProperties.reduce((acc, p) => acc + p.constructionProgress, 0) / mockProperties.length);
  const nextPayment = mockProperties.filter(p => p.nextInstallment).sort((a, b) => new Date(a.nextInstallment!.dueDate).getTime() - new Date(b.nextInstallment!.dueDate).getTime())[0];
  // Get the most recent announcement (first one in the array)
  const featuredAnnouncement = mockAnnouncements[0];
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(isRTL ? 'ar-AE' : 'en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };
  const formatFullDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(isRTL ? 'ar-AE' : 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getDaysUntil = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  const categoryColors = {
    general: {
      bg: '#dbeafe',
      text: '#1e40af'
    },
    construction: {
      bg: '#fef3c7',
      text: '#92400e'
    },
    payment: {
      bg: '#d1fae5',
      text: '#065f46'
    },
    event: {
      bg: '#fce7f3',
      text: '#9f1239'
    },
    maintenance: {
      bg: '#e0e7ff',
      text: '#3730a3'
    }
  };
  const priorityConfig = {
    low: {
      color: '#64748b',
      labelKey: 'announcements.info'
    },
    medium: {
      color: '#f59e0b',
      labelKey: 'announcements.important'
    },
    high: {
      color: '#ef4444',
      labelKey: 'announcements.urgent'
    }
  };
  const categoryColor = categoryColors[featuredAnnouncement.category];
  const priority = priorityConfig[featuredAnnouncement.priority];
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;
  return <>
      <ScrollArea h="100%" offsetScrollbars data-id="element-181">
        <Stack gap="lg" pb={100} data-id="element-182">
          {/* Header */}
          <Box p="lg" style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #2d3a4f 100%)',
          borderRadius: '0 0 24px 24px',
          marginTop: -16,
          marginLeft: -16,
          marginRight: -16,
          paddingTop: 32
        }} data-id="element-183">
            <Group justify="space-between" align="flex-start" mb="lg" data-id="element-184">
              <Stack gap={4} data-id="element-185">
                <Text size="sm" c="rgba(255,255,255,0.7)" data-id="element-186">
                  {t('dashboard.welcome')},
                </Text>
                <Text fw={700} size="xl" c="white" data-id="element-187">
                  Ahmed Al-Rashid
                </Text>
              </Stack>
              <Group gap="sm" data-id="element-188">
                <LanguageSwitcher data-id="element-189" />
                <Box p={10} style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: 12,
                cursor: 'pointer'
              }} data-id="element-190">
                  <Bell size={22} color="white" data-id="element-191" />
                </Box>
              </Group>
            </Group>

            {/* Quick Stats */}
            <Group grow gap="md" data-id="element-192">
              <Card p="md" radius="md" style={{
              backgroundColor: 'rgba(255,255,255,0.1)'
            }} data-id="element-193">
                <Stack gap={4} data-id="element-194">
                  <Text size="xs" c="rgba(255,255,255,0.7)" data-id="element-195">
                    {t('dashboard.myProperties')}
                  </Text>
                  <Text fw={700} size="xl" c="white" data-id="element-196">
                    {mockProperties.length}
                  </Text>
                </Stack>
              </Card>
              <Card p="md" radius="md" style={{
              backgroundColor: 'rgba(255,255,255,0.1)'
            }} data-id="element-197">
                <Stack gap={4} data-id="element-198">
                  <Text size="xs" c="rgba(255,255,255,0.7)" data-id="element-199">
                    {t('dashboard.avgProgress')}
                  </Text>
                  <Text fw={700} size="xl" c="white" data-id="element-200">
                    {totalProgress}%
                  </Text>
                </Stack>
              </Card>
            </Group>
          </Box>

          {/* Featured Announcement */}
          <Card shadow="sm" padding="md" radius="md" withBorder onClick={() => setAnnouncementModalOpen(true)} style={{
          cursor: 'pointer'
        }} styles={{
          root: {
            borderColor: featuredAnnouncement.priority === 'high' ? '#fecaca' : '#e2e8f0',
            borderLeft: isRTL ? 'none' : `4px solid ${priority.color}`,
            borderRight: isRTL ? `4px solid ${priority.color}` : 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: featuredAnnouncement.priority === 'high' ? '#fca5a5' : '#cbd5e1',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }
          }
        }} data-id="element-201">
            <Stack gap="sm" data-id="element-202">
              <Group justify="space-between" align="flex-start" data-id="element-203">
                <Group gap="sm" data-id="element-204">
                  <Box p={10} style={{
                  backgroundColor: categoryColor.bg,
                  borderRadius: 10
                }} data-id="element-205">
                    <Megaphone size={20} color={categoryColor.text} data-id="element-206" />
                  </Box>
                  <Stack gap={4} data-id="element-207">
                    <Text size="xs" c="#64748b" tt="uppercase" fw={500} data-id="element-208">
                      {t('dashboard.latestAnnouncement')}
                    </Text>
                    <Text fw={700} size="md" c="#1a2332" data-id="element-209">
                      {featuredAnnouncement.title}
                    </Text>
                  </Stack>
                </Group>
                <ChevronIcon size={20} color="#94a3b8" data-id="element-210" />
              </Group>

              <Text size="sm" c="#64748b" lineClamp={2} data-id="element-211">
                {featuredAnnouncement.summary}
              </Text>

              <Group justify="space-between" align="center" data-id="element-212">
                <Group gap="xs" data-id="element-213">
                  <Calendar size={12} color="#94a3b8" data-id="element-214" />
                  <Text size="xs" c="#94a3b8" data-id="element-215">
                    {formatFullDate(featuredAnnouncement.publishedAt)}
                  </Text>
                </Group>
                {featuredAnnouncement.priority !== 'low' && <Box px="sm" py={4} style={{
                backgroundColor: featuredAnnouncement.priority === 'high' ? '#fee2e2' : '#fef3c7',
                borderRadius: 12
              }} data-id="element-216">
                    <Text size="xs" fw={600} c={priority.color} data-id="element-217">
                      {t(priority.labelKey)}
                    </Text>
                  </Box>}
              </Group>
            </Stack>
          </Card>

          {/* Next Installment Alert */}
          {nextPayment?.nextInstallment && <Card shadow="sm" p="md" radius="md" style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a'
        }} data-id="element-218">
              <Group justify="space-between" align="center" data-id="element-219">
                <Group gap="md" data-id="element-220">
                  <Box p={10} style={{
                backgroundColor: '#fef3c7',
                borderRadius: 10
              }} data-id="element-221">
                    <Calendar size={20} color="#d97706" data-id="element-222" />
                  </Box>
                  <Stack gap={2} data-id="element-223">
                    <Text size="xs" c="#92400e" fw={500} data-id="element-224">
                      {t('dashboard.nextPaymentDue')}
                    </Text>
                    <Text fw={700} size="md" c="#1a2332" data-id="element-225">
                      {formatCurrency(nextPayment.nextInstallment.amount)}
                    </Text>
                  </Stack>
                </Group>
                <Stack gap={0} align={isRTL ? 'flex-start' : 'flex-end'} data-id="element-226">
                  <Text size="lg" fw={700} c="#d97706" data-id="element-227">
                    {getDaysUntil(nextPayment.nextInstallment.dueDate)}
                  </Text>
                  <Text size="xs" c="#92400e" data-id="element-228">
                    {t('common.daysLeft')}
                  </Text>
                </Stack>
              </Group>
            </Card>}

          {/* Construction Progress Widget */}
          <Card shadow="sm" p="md" radius="md" withBorder data-id="element-229">
            <Group justify="space-between" mb="md" data-id="element-230">
              <Group gap="sm" data-id="element-231">
                <TrendingUp size={18} color="#10b981" data-id="element-232" />
                <Text fw={600} size="sm" c="#1a2332" data-id="element-233">
                  {t('dashboard.overallConstruction')}
                </Text>
              </Group>
              <Text fw={700} size="lg" c="#10b981" data-id="element-234">
                {totalProgress}%
              </Text>
            </Group>
            <Progress value={totalProgress} size="lg" radius="xl" color="#10b981" styles={{
            root: {
              backgroundColor: '#e2e8f0'
            }
          }} data-id="element-235" />
            <Text size="xs" c="#64748b" mt="sm" data-id="element-236">
              {t('dashboard.avgProgressDesc')}
            </Text>
          </Card>

          {/* My Properties Section */}
          <Stack gap="sm" data-id="element-237">
            <Group justify="space-between" align="center" data-id="element-238">
              <Text fw={700} size="lg" c="#1a2332" data-id="element-239">
                {t('dashboard.myProperties')}
              </Text>
              <Group gap={4} style={{
              cursor: 'pointer'
            }} onClick={onViewAllProperties} data-id="element-240">
                <Text size="sm" fw={500} c="#10b981" data-id="element-241">
                  {t('common.viewAll')}
                </Text>
                <ChevronIcon size={16} color="#10b981" data-id="element-242" />
              </Group>
            </Group>

            <Stack gap="md" data-id="element-243">
              {mockProperties.map(property => <PropertyCard key={property.id} property={property} onClick={() => onPropertyClick?.(property.id)} data-id="element-244" />)}
            </Stack>
          </Stack>
        </Stack>
      </ScrollArea>

      {/* Announcement Detail Modal */}
      <Modal opened={announcementModalOpen} onClose={() => setAnnouncementModalOpen(false)} title={<Stack gap="xs" data-id="element-246">
            <Text fw={700} size="lg" c="#1a2332" data-id="element-247">
              {featuredAnnouncement.title}
            </Text>
            <Group gap="xs" data-id="element-248">
              <Text size="xs" c="#64748b" tt="uppercase" fw={500} data-id="element-249">
                {featuredAnnouncement.category}
              </Text>
              <Text size="xs" c="#94a3b8" data-id="element-250">
                •
              </Text>
              <Text size="xs" c="#64748b" data-id="element-251">
                {formatFullDate(featuredAnnouncement.publishedAt)}
              </Text>
            </Group>
          </Stack>} radius="md" centered size="lg" styles={{
      body: {
        padding: 0
      },
      header: {
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0'
      }
    }} data-id="element-245">
        <Stack gap={0} data-id="element-252">
          {/* Image */}
          {featuredAnnouncement.image && <Box data-id="element-253">
              <Image src={featuredAnnouncement.image} alt={featuredAnnouncement.title} height={240} fit="cover" data-id="element-254" />
            </Box>}

          {/* Content */}
          <ScrollArea h={400} p="lg" data-id="element-255">
            <Stack gap="md" data-id="element-256">
              <Text size="sm" c="#1a2332" style={{
              whiteSpace: 'pre-line',
              lineHeight: 1.6
            }} data-id="element-257">
                {featuredAnnouncement.content}
              </Text>

              {featuredAnnouncement.attachments && featuredAnnouncement.attachments.length > 0 && <>
                    <Divider data-id="element-258" />
                    <Stack gap="sm" data-id="element-259">
                      <Text fw={600} size="sm" c="#1a2332" data-id="element-260">
                        Attachments
                      </Text>
                      {featuredAnnouncement.attachments.map((attachment, index) => <Group key={index} justify="space-between" p="sm" style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0'
                }} data-id="element-261">
                            <Text size="sm" c="#1a2332" data-id="element-262">
                              {attachment.name}
                            </Text>
                            <Button variant="subtle" size="xs" leftSection={<Download size={14} data-id="element-264" />} data-id="element-263">
                              {t('common.download')}
                            </Button>
                          </Group>)}
                    </Stack>
                  </>}
            </Stack>
          </ScrollArea>

          {/* Footer */}
          <Box p="md" style={{
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc'
        }} data-id="element-265">
            <Group justify="space-between" data-id="element-266">
              <Text size="xs" c="#64748b" data-id="element-267">
                {t('announcements.forQuestions')}
              </Text>
              <Button variant="light" size="xs" color="blue" leftSection={<ExternalLink size={14} data-id="element-269" />} data-id="element-268">
                {t('common.share')}
              </Button>
            </Group>
          </Box>
        </Stack>
      </Modal>
    </>;
}